import { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import API from "../lib/api";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  Plus, Receipt, ArrowLeft, MagnifyingGlass, ArrowRight, MagicWand, Trash,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../components/ui/dialog";
import CreateInHousePoDialog from "../components/CreateInHousePoDialog";
import BulkGenerateInHousePosDialog from "../components/BulkGenerateInHousePosDialog";

const STATUS = {
  draft:        { label: "Draft",        cls: "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300" },
  issued:       { label: "Issued",       cls: "bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300" },
  acknowledged: { label: "Acknowledged", cls: "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300" },
  paid:         { label: "Paid",         cls: "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300" },
};

export default function InHousePosPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [pos, setPos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [showCreate, setShowCreate] = useState(false);
  const [showBulk, setShowBulk] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);  // PO object pending delete
  const [deleting, setDeleting] = useState(false);

  const fetchPos = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await API.get("/in-house/pos");
      setPos(data.pos || []);
    } catch { toast.error("Failed to load purchase orders"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchPos(); }, [fetchPos]);

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await API.delete(`/in-house/pos/${deleteTarget.id}`);
      toast.success(`Deleted ${deleteTarget.po_number}`);
      setDeleteTarget(null);
      fetchPos();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't delete PO");
    } finally {
      setDeleting(false);
    }
  };

  // Read query params: ?status=draft|issued|acknowledged|paid pre-filters the
  // list; ?bulk=1 auto-opens the bulk-generate dialog. Both are stripped after
  // applying so refreshes don't re-trigger them.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const statusParam = params.get("status");
    const bulkParam = params.get("bulk");
    let didSomething = false;
    if (statusParam && ["draft", "issued", "acknowledged", "paid"].includes(statusParam)) {
      setStatusFilter(statusParam);
      didSomething = true;
    }
    if (bulkParam === "1") {
      setShowBulk(true);
      didSomething = true;
    }
    if (didSomething) {
      navigate("/in-house/pos", { replace: true });
    }
  }, [location.search, navigate]);

  const filtered = pos.filter(p => {
    if (statusFilter !== "all" && p.status !== statusFilter) return false;
    const q = search.toLowerCase();
    if (!q) return true;
    return [p.po_number, p.operative_name, p.title].some(v => (v || "").toLowerCase().includes(q));
  });

  // Counts by status (used by the filter chips)
  const counts = pos.reduce((acc, p) => {
    acc[p.status] = (acc[p.status] || 0) + 1;
    return acc;
  }, {});
  counts.all = pos.length;

  return (
    <div className="p-6 space-y-6" data-testid="in-house-pos-page">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <Button variant="ghost" size="sm" onClick={() => navigate("/in-house/team")} data-testid="in-house-pos-back-btn">
            <ArrowLeft size={14} className="mr-1" /> Back to team
          </Button>
          <h1 className="text-3xl font-bold font-[Chivo] flex items-center gap-2 mt-2">
            <Receipt className="h-7 w-7 text-amber-600" weight="duotone" /> In-House Purchase Orders
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Issue POs to your self-employed operatives so they can invoice you. One row per job with units, rates and live totals — all on one branded PDF.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={() => setShowBulk(true)}
            data-testid="in-house-pos-bulk-generate-btn"
          >
            <MagicWand size={14} className="mr-1" /> Generate POs (this week)
          </Button>
          <Button onClick={() => setShowCreate(true)} data-testid="in-house-pos-create-btn">
            <Plus size={14} className="mr-1" /> New Purchase Order
          </Button>
        </div>
      </div>

      {/* Status filter chips */}
      <div className="flex items-center gap-2 flex-wrap" data-testid="in-house-pos-status-filter">
        {[
          { key: "all",          label: "All"          },
          { key: "draft",        label: "Draft"        },
          { key: "issued",       label: "Issued"       },
          { key: "acknowledged", label: "Acknowledged" },
          { key: "paid",         label: "Paid"         },
        ].map(opt => {
          const active = statusFilter === opt.key;
          const n = counts[opt.key] || 0;
          return (
            <button
              key={opt.key}
              onClick={() => setStatusFilter(opt.key)}
              className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                active
                  ? "bg-primary text-primary-foreground border-primary"
                  : "bg-card text-muted-foreground border-border hover:bg-muted hover:text-foreground"
              }`}
              data-testid={`in-house-pos-filter-${opt.key}`}
            >
              {opt.label}
              <span className={`ml-1.5 ${active ? "opacity-80" : "opacity-60"}`}>· {n}</span>
            </button>
          );
        })}
      </div>

      {/* Search */}
      <div className="relative max-w-sm">
        <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <input
          type="text"
          placeholder="Search by PO number, operative or title…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full pl-9 pr-3 py-2 text-sm border border-border rounded-md bg-card"
          data-testid="in-house-pos-search"
        />
      </div>

      {/* List */}
      {loading ? (
        <div className="text-center py-12 text-sm text-muted-foreground">Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="border-2 border-dashed border-border rounded-lg p-16 text-center" data-testid="in-house-pos-empty">
          <Receipt className="h-12 w-12 mx-auto text-muted-foreground" weight="duotone" />
          {pos.length === 0 ? (
            <>
              <p className="mt-4 font-semibold">No purchase orders yet</p>
              <p className="text-sm text-muted-foreground mt-1">Create your first PO to issue to a self-employed operative.</p>
              <Button onClick={() => setShowCreate(true)} className="mt-4">
                <Plus size={14} className="mr-1" /> Create a PO
              </Button>
            </>
          ) : (
            <>
              <p className="mt-4 font-semibold">No {statusFilter === "all" ? "matching" : statusFilter} purchase orders</p>
              <p className="text-sm text-muted-foreground mt-1">
                Try a different filter or clear your search to see all {pos.length} POs.
              </p>
              {statusFilter !== "all" && (
                <Button variant="outline" onClick={() => setStatusFilter("all")} className="mt-4" data-testid="in-house-pos-clear-filter-btn">
                  Show all POs
                </Button>
              )}
            </>
          )}
        </div>
      ) : (
        <div className="bg-card border border-border rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-xs uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="px-4 py-3 text-left font-semibold">PO #</th>
                <th className="px-4 py-3 text-left font-semibold">Operative</th>
                <th className="px-4 py-3 text-left font-semibold hidden md:table-cell">Period</th>
                <th className="px-4 py-3 text-right font-semibold">Total</th>
                <th className="px-4 py-3 text-left font-semibold">Status</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(p => (
                <tr
                  key={p.id}
                  className="border-t border-border hover:bg-muted/30 cursor-pointer transition"
                  onClick={() => navigate(`/in-house/pos/${p.id}`)}
                  data-testid={`in-house-po-row-${p.po_number}`}
                >
                  <td className="px-4 py-3 font-mono text-xs">{p.po_number}</td>
                  <td className="px-4 py-3">
                    <div className="font-medium">{p.operative_name}</div>
                    <div className="text-xs text-muted-foreground">{p.title}</div>
                  </td>
                  <td className="px-4 py-3 hidden md:table-cell text-xs text-muted-foreground">
                    {p.period_start || "—"} → {p.period_end || "—"}
                  </td>
                  <td className="px-4 py-3 text-right font-semibold">
                    £{Number(p.total || 0).toLocaleString("en-GB", { minimumFractionDigits: 2 })}
                  </td>
                  <td className="px-4 py-3">
                    <Badge className={(STATUS[p.status] || STATUS.draft).cls}>
                      {(STATUS[p.status] || STATUS.draft).label}
                    </Badge>
                  </td>
                  <td className="px-4 py-3 text-right text-muted-foreground">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); setDeleteTarget(p); }}
                        className="opacity-60 hover:opacity-100 hover:text-destructive transition p-1 rounded"
                        title="Delete this PO"
                        data-testid={`in-house-po-delete-btn-${p.po_number}`}
                      >
                        <Trash size={14} />
                      </button>
                      <ArrowRight size={14} />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <CreateInHousePoDialog
        open={showCreate}
        onClose={() => setShowCreate(false)}
        onCreated={(po) => { setShowCreate(false); navigate(`/in-house/pos/${po.id}`); }}
      />

      <BulkGenerateInHousePosDialog
        open={showBulk}
        onClose={() => setShowBulk(false)}
        onCreated={() => { fetchPos(); }}
      />

      {/* Delete confirmation */}
      <Dialog open={!!deleteTarget} onOpenChange={(o) => { if (!o && !deleting) setDeleteTarget(null); }}>
        <DialogContent className="max-w-md" data-testid="in-house-po-delete-dialog">
          <DialogHeader>
            <DialogTitle className="font-[Chivo] flex items-center gap-2">
              <Trash size={18} className="text-destructive" /> Delete {deleteTarget?.po_number}?
            </DialogTitle>
            <DialogDescription>
              {deleteTarget?.status === "draft" ? (
                <>This draft will be permanently removed. You can&apos;t undo this.</>
              ) : (
                <>
                  This PO is <strong>{deleteTarget?.status}</strong> with a total of <strong>£{Number(deleteTarget?.total || 0).toLocaleString("en-GB", { minimumFractionDigits: 2 })}</strong>.
                  Deleting it removes the record from {deleteTarget?.operative_name}&apos;s portal too. The deletion is audit-logged but the PO can&apos;t be restored.
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTarget(null)} disabled={deleting} data-testid="in-house-po-delete-cancel">
              Cancel
            </Button>
            <Button
              onClick={handleDelete}
              disabled={deleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="in-house-po-delete-confirm"
            >
              <Trash size={14} className="mr-1" /> {deleting ? "Deleting…" : "Delete PO"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
